import { scanCategoryData } from '../views/scan/ScannerCategory';

const typeMap = {
  0: 'range',
  2: 'select',
  3: 'multiselect'
};

/**
 * Maps range field IDs to their unit translation key suffix.
 * Used to format range values with the correct unit (e.g. '億', '％').
 */
export const RANGE_UNIT_MAP = {
  MarginTurnoverPeriod: 'days',
  AnalystCount: 'rating',
  Rating: 'rating',
  BPS: 'yen',
  DividendPerShare: 'yen',
  DividendPerShareForecast: 'yen',
  EarningsPerShare: 'yen',
  MarginLendingFee: 'yen',
  PredictedEarningsPerShare: 'yen',
  InvestmentAmount: 'yen',
  NetAssets: 'billion',
  PredictedNetProfit: 'billion',
  PredictedNetSales: 'billion',
  PredictedOrdinaryProfit: 'billion',
  TotalAssets: 'billion',
  MarketCap: 'billion',
  NetProfit: 'billion',
  NetSales: 'billion',
  OrdinaryProfit: 'billion',
  InventoryTurnoverRate: 'multiple',
  MarginBuySellRatio: 'multiple',
  PBR: 'multiple',
  PredictedPriceEarningsRatio: 'multiple',
  PredictedPriceSalesRatio: 'multiple',
  PriceCashFlowRatio: 'multiple',
  PriceEarningsRatio: 'multiple',
  PriceSalesRatio: 'multiple',
  ReceivablesTurnoverRate: 'multiple',
  TotalScore: 'points',
  FiscalHealthScore: 'points',
  GrowthScore: 'points',
  MarketTrendScore: 'points',
  SizeScore: 'points',
  TechnicalScore: 'points',
  ValueScore: 'points',
  ChangeInSharesBoughtOnMargin: 'shares',
  ChangeInSharesSoldOnMargin: 'shares',
  SharesBoughtOnMargin: 'shares',
  SharesSoldOnMargin: 'shares',
  LotSize: 'shares',
  PredictedOrdinaryProfitChange: 'percent',
  CapitalAdequacyRatio: 'percent',
  CurrentRatio: 'percent',
  DebtEquityRatio: 'percent',
  DividendOnEquity: 'percent',
  DividendPayoutRatio: 'percent',
  DividendYield: 'percent',
  DividendYieldForecast: 'percent',
  PredictedNetProfitChange: 'percent',
  PredictedNetSalesChange: 'percent',
  QuickRatio: 'percent',
  ReturnOnAssets: 'percent',
  ReturnOnEquity: 'percent',
  SMADeviation_130: 'percent',
  SMADeviation_25: 'percent',
  SMADeviation_5: 'percent',
  SMADeviation_65: 'percent',
  TargetPriceChange: 'percent',
  TargetPriceVsCurrentPrice: 'percent',
  NetProfitChange: 'percent',
  NetSalesChange: 'percent',
  OrdinaryProfitChange: 'percent',
  OrdinaryProfitToNetSalesRatio: 'percent',
  OrdinaryProfitVsPredictedProgress: 'percent',
  StochasticFast_9: 'percent',
  StochasticSlow_9: 'percent',
  RSI_14: 'percent',
  ATR_14: 'percent',
  MFI_14: 'percent',
  RCI_10_3: 'percent',
  CCI_14_9: 'percent'
};

/**
 * Format a single scan field's values into a human-readable string.
 *
 * @param {Object}   field    – { id, type, values }
 * @param {Function} localize – the useLocalize hook result
 * @returns {string|null} formatted value, or null if nothing to display
 */
export function formatFieldValue(field, localize) {
  if (!field) {
    return null;
  }

  const { id, type, values: rawValues } = field;
  const values = rawValues ?? [];
  const noSettings = localize('scan.criteria.no.settings');

  // Typed fields with no values show 設定なし
  if (type && values.length === 0) {
    return noSettings;
  }

  // Untyped fields with no values use the caller's fallback
  if (!type && values.length === 0) {
    return null;
  }

  switch (type) {
    case 'select': {
      // Single select – values[0] is the selected option key
      if (!values[0] && values[0] !== 0) return null;
      return localize(`scan.criteria.${id}.${values[0]}`);
    }

    case 'multiselect': {
      const comma = localize('common.comma');
      return values
        .map((v) => localize(`scan.criteria.${id}.${v}`))
        .join(comma);
    }

    case 'range': {
      const lowerExists = typeof values[0] === 'number';
      const upperExists = typeof values[1] === 'number';

      if (!lowerExists && !upperExists) return noSettings;

      const unitKey = RANGE_UNIT_MAP[id];
      if (!unitKey) return null;

      const unit = localize(`scan.criteria.range.${unitKey}`).trim();

      if (lowerExists && !upperExists) {
        return localize('scan.criteria.range.upper', {
          0: values[0],
          u: unit
        });
      }
      if (!lowerExists && upperExists) {
        return localize('scan.criteria.range.lower', {
          1: values[1],
          u: unit
        });
      }
      return localize('scan.criteria.range', {
        0: values[0],
        1: values[1],
        u: unit
      });
    }

    default: {
      // Attempt translation for each value; fall back to the raw value if missing
      return values
        .map((v) => {
          const key = `scan.criteria.${id}.${v}`;
          const translated = localize(key);
          return translated !== key ? translated : v;
        })
        .join(', ');
    }
  }
}

/**
 * Resolve a human-readable label for a scan field.
 * Prefers the desktop-scan-specific key, falls back to the mobile scan key,
 * then the ResourceKey from ScannerCategory metadata, then the raw field id.
 *
 * @param {string}   fieldId  – scan field id (e.g. 'MarketCap')
 * @param {Function} localize – the useLocalize hook result
 * @returns {string}
 */
export function getFieldLabel(fieldId, localize) {
  // Desktop scan criteria keys (e.g. desktop.scans.criteria.MarketCap)
  const desktopKey = `desktop.scans.criteria.${fieldId}`;
  const desktopLabel = localize(desktopKey);
  if (desktopLabel && desktopLabel !== desktopKey) {
    return desktopLabel;
  }

  // Mobile scan criteria keys (e.g. scan.criteria.MarketCap)
  const mobileKey = `scan.criteria.${fieldId}`;
  const mobileLabel = localize(mobileKey);
  if (mobileLabel && mobileLabel !== mobileKey) {
    return mobileLabel;
  }

  // Last resort – use the ResourceKey from category metadata
  const meta = scanCategoryData[fieldId];
  if (meta?.ResourceKey) {
    const resLabel = localize(meta.ResourceKey);
    if (resLabel && resLabel !== meta.ResourceKey) {
      return resLabel;
    }
  }

  return fieldId;
}

export function getRegexFromCategory(category, options, scanCategoryData) {
  return options.map((option) => {
    return new RegExp(scanCategoryData[category].Options[option].Pattern);
  });
}

export function filterSelects(
  symbolData,
  criteria,
  scanCategoryData,
  previousResults = null
) {
  // This gets a list of regex expressions matching the possible options
  // (such as ETF, REIT, Equity) for this selectable scan category
  let regexToTest = getRegexFromCategory(
    criteria.id,
    criteria.values,
    scanCategoryData
  );

  return symbolData.filter(function (s) {
    // If there are previous results and this symbol is not
    // included, don't even bother
    if (previousResults && !previousResults[s.symbol]) {
      return false;
    }

    // If there are no selected values for this criteria, all symbols
    // are considered to "match"
    return (
      criteria.values.length === 0 ||
      regexToTest.some((regex) => regex.test(s.data.filterValue))
    );
  });
}

export function filterRange(
  symbolData,
  criteria,
  scanCategoryData,
  previousResults = null
) {
  return symbolData.filter((s) => {
    if (s.data.filterValue == null) {
      return false;
    }

    // If there are previous results and this symbol is not
    // included, don't even bother
    if (previousResults && !previousResults[s.symbol]) {
      return false;
    }

    let isGreaterThan =
      // userScanSetting is expected to have 'values', where [0]
      // is the minimum value, and [1] is the maximum value.
      typeof criteria.values[0] == 'number'
        ? criteria.values[0] <= s.data.filterValue
        : true;
    let isLessThan =
      typeof criteria.values[1] == 'number'
        ? s.data.filterValue <= criteria.values[1]
        : true;

    return isGreaterThan && isLessThan;
  });
}

export function getCriteriaOptions(field) {
  switch (field.type) {
    case 'select':
      return field.values[0];
    case 'multiselect':
      return field.values;
    case 'range':
      return {
        lower: field.values[0],
        upper: field.values[1]
      };
    default:
      return null;
  }
}

export function getCriteriaFromScan(scan) {
  let criteria = [],
    criteriaOptions = {};

  if (scan) {
    scan.fields.forEach((field) => {
      criteria.push(field.id);
      criteriaOptions[field.id] = getCriteriaOptions(field);
    });
  }
  return [criteria, criteriaOptions];
}

export function buildScan(criteria, selections, scanName, scanId) {
  let scan = {
    name: scanName,
    fields: []
  };

  if (scanId) {
    scan.id = scanId;
  }

  if (!criteria || !selections || !scanName) {
    let message = `[buildScan] Insufficient parameter`;
    console.log(message);
    throw new Error(message);
  }

  for (let i = 0; i < criteria.length; i++) {
    let criterion = criteria[i];
    let criterionData = scanCategoryData[criterion];
    let type = typeMap[criterionData.Input];

    let values = [];

    let selectedValues = selections[criterion];

    switch (type) {
      case 'select':
        // This value is null when no option is selected,
        // or is the string value of the option when selected
        if (selectedValues) {
          values.push(selectedValues);
        }
        break;
      case 'multiselect':
        // This value is null when no option has ever been selected,
        // otherwise it is an array (maybe empty) of all currently
        // selected options
        if (selectedValues) {
          values = selectedValues;
        }
        break;
      case 'range':
        // This value is null when no values have ever been entered,
        // otherwise it is an object with two potential members of
        // lower and upper.  When the value for either is empty, the
        // value is set to an empty string, otherwise it is a number
        // in a string representing the correct value
        if (selectedValues) {
          let lowerValue =
            !selectedValues.lower || selectedValues.lower === ''
              ? null
              : parseFloat(selectedValues.lower);
          let upperValue =
            !selectedValues.upper || selectedValues.upper === ''
              ? null
              : parseFloat(selectedValues.upper);

          values = [lowerValue, upperValue];
        }
        break;
      default: {
        let message = `[buildScan] Invalid type specified: ${type}`;
        console.log(message);
        throw new Error(message);
      }
    }

    scan.fields.push({
      id: criterion,
      type: type,
      values: values
    });
  }

  return scan;
}
